import { useContext, useEffect } from 'react';

import { GlobalModalContext } from './contexts';
import type { CurrentModalPayload, GlobalModalProps, GlobalModalReturn, ModalType } from './types';
/**
 * Hook to use the GlobalModal context in functional components.
 *
 * @param props - The props required to configure the modal context.
 * Tracking source is used in some modals to log modal open locations.
 * Registered modals is an array of modal types that are available to open - only these will render on the page.
 * @returns An object with methods to open and close modals and the current modal state.
 */

export function useGlobalModal<T extends ModalType>({
  registeredModals,
}: GlobalModalProps<T>): GlobalModalReturn<T> {
  const { openModal, closeModal, registerModals, currentModal } = useContext(GlobalModalContext);

  useEffect(() => {
    registerModals(registeredModals);
  }, [registeredModals, registerModals]);

  // Only allow payloads whose type is in registeredModals
  const openTypedModal = (payload: Extract<CurrentModalPayload, { type: T }>) => {
    if (registeredModals.includes(payload.type as T)) {
      openModal(payload);
    } else {
      console.warn(`Modal type ${payload.type} is not registered for this hook instance.`);
    }
  };

  return {
    currentModal: currentModal as T | null,
    openModal: openTypedModal,
    closeModal,
  };
}

/**
 * Example usage in a component:
 *
 * import React from 'react';
 * import { useGlobalModal, ModalType } from './path/to/GlobalModalContext';
 *
 * const ExampleComponent: React.FC = () => {
 *   const { openModal } = useGlobalModal({
 *     registeredModals: [ModalType.UPGRADE]
 *   });
 *
 *   const handleOpenModal = () => {
 *     openModal({ type: ModalType.UPGRADE, trackingSource: 'card-settings-dialog' });
 *   };
 *
 *   return (
 *     <button onClick={handleOpenModal}>
 *       Open Upgrade Modal
 *     </button>
 *   );
 * };
 */
